"use client";

import { Link } from "@frontend/lib/link";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BoostTierIcon } from "@frontend/components/boost-tier-icon";
import { RevealOnScroll } from "@frontend/components/reveal-on-scroll";
import { TrackScoreBadge } from "@frontend/components/track-score-badge";
import { Avatar, cx, FilterEmptyIcon, InboxEmptyIcon } from "@frontend/components/ui";
import { collabCounterpartAvatarUrl } from "@frontend/lib/avatar";
import { collabPreview } from "@frontend/lib/collab-preview";
import {
  countByStatus,
  collabMatchesStatus,
  isHistoryCollab,
  STATUS_FILTERS,
  type StatusFilter,
} from "@frontend/lib/collab-status";
import { collabMatchesTab, collabNeedsYou } from "@frontend/lib/collab-tabs";
import { listCounterpartTrack } from "@frontend/lib/reliability";
import {
  sideToRole,
  useWorkspaceSide,
  WORKSPACE_SIDE_LABELS,
} from "@frontend/lib/workspace-side";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";
import { formatINR } from "@creators/backend/lib/money";
import { formatInboxStamp } from "@creators/backend/lib/time";

/**
 * The inbox. It is the whole of /collabs and the left pane of a collab room, because
 * a collab is a conversation: the list and the thread belong on one screen.
 */
export function CollabRail({
  collabs,
  activeId = null,
}: {
  collabs: CollabListRow[];
  /** Null on /collabs, where the thread pane is still waiting to be filled. */
  activeId?: string | null;
}) {
  const navigate = useNavigate();
  const { side, canToggle } = useWorkspaceSide();
  const roleSide = canToggle ? sideToRole(side) : null;
  const [query, setQuery] = useState("");
  const [needsYou, setNeedsYou] = useState(false);

  // Collabs for the selected side only — status chips and the list share this set.
  const scoped = useMemo(() => {
    // Chat inbox is live work only — completed / cancelled / expired live in History.
    const open = collabs.filter((collab) => !isHistoryCollab(collab));
    return roleSide ? open.filter((collab) => collab.yourRole === roleSide) : open;
  }, [collabs, roleSide]);

  // Live work first, since that is what someone opening their inbox came for —
  // unless there is none, in which case an empty list would be a poor greeting.
  const [status, setStatus] = useState<StatusFilter>("active");

  // When the side changes, prefer Active if that side has live work.
  useEffect(() => {
    setStatus(scoped.some((collab) => collabMatchesStatus(collab, "active")) ? "active" : "all");
  }, [roleSide]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-pick when side flips

  // Leaving a room that belongs to the other side — otherwise the open thread
  // stays painted and the toggle looks broken.
  useEffect(() => {
    if (!activeId || !roleSide) return;
    const active = collabs.find((collab) => collab.id === activeId);
    if (active && active.yourRole !== roleSide) {
      navigate("/collabs", { replace: true });
    }
  }, [activeId, roleSide, collabs, navigate]);

  const actionCount = useMemo(
    () => scoped.filter((collab) => collabMatchesTab(collab, "action")).length,
    [scoped],
  );

  const counts = useMemo(() => countByStatus(scoped), [scoped]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return scoped.filter((collab) => {
      // Keep the open collab visible through status / needs-you filters, but never
      // across Creator ↔ Collaborator — that would make the toggle look inert.
      // History collabs are already excluded from `scoped`.
      if (!needle && collab.id === activeId) return true;
      if (needsYou && !collabMatchesTab(collab, "action")) return false;
      if (!collabMatchesStatus(collab, status)) return false;
      if (!needle) return true;
      return (
        collab.counterpartName.toLowerCase().includes(needle) ||
        collab.reference.toLowerCase().includes(needle)
      );
    });
  }, [scoped, query, needsYou, status, activeId]);

  const emptyMessage = (() => {
    if (query.trim()) return "No results.";
    if (needsYou || status !== "all") return "Nothing under this filter.";
    if (roleSide) {
      return `No ${WORKSPACE_SIDE_LABELS[side].toLowerCase()} collabs.`;
    }
    return "Nothing here yet.";
  })();

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      <div className="shrink-0 space-y-2.5 px-3 pt-3 pb-2.5">
        <div className="relative">
          <span
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-subtle"
            aria-hidden="true"
          >
            <SearchIcon />
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search collabs"
            aria-label="Search collabs"
            className="w-full rounded-full border border-line bg-canvas/60 py-2.5 pr-3 pl-9 text-base text-ink outline-none transition placeholder:text-ink-subtle focus:border-brand-500 focus:bg-surface"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <RailFilterMenu
            label="Filter by status"
            value={status}
            onChange={setStatus}
            options={STATUS_FILTERS.map((option) => ({
              id: option.id,
              label: option.label,
              count: option.id === "all" ? undefined : counts[option.id],
              disabled: counts[option.id] === 0,
            }))}
          />

          <button
            type="button"
            onClick={() => setNeedsYou((value) => !value)}
            aria-pressed={needsYou}
            className={chipClass(needsYou)}
          >
            {needsYou ? (
              <svg
                viewBox="0 0 16 16"
                fill="currentColor"
                className="size-3.5 text-money-600"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm3.844-8.791a.75.75 0 0 0-1.188-.918l-3.7 4.79-1.65-1.874a.75.75 0 1 0-1.114 1.004l2.25 2.555a.75.75 0 0 0 1.15-.037l4.252-5.52Z"
                  clipRule="evenodd"
                />
              </svg>
            ) : null}
            Needs you
            {actionCount > 0 ? (
              <span className="tabular text-[10px] text-ink-subtle">{actionCount}</span>
            ) : null}
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1.5 pb-3 [scrollbar-gutter:stable]">
        {visible.length === 0 ? (
          <RailEmpty
            icon={
              query.trim() || needsYou || roleSide || status !== "all" ? (
                <FilterEmptyIcon className="size-5" />
              ) : (
                <InboxEmptyIcon className="size-5" />
              )
            }
            message={emptyMessage}
          />
        ) : (
          <ul>
            <RevealOnScroll resetKey={`${side}:${query}:${status}:${needsYou}`}>
              {visible.map((collab) => (
                <li key={collab.id}>
                  <RailRow collab={collab} active={collab.id === activeId} />
                </li>
              ))}
            </RevealOnScroll>
          </ul>
        )}
      </div>
    </div>
  );
}

function chipClass(active: boolean): string {
  return `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition ${
    active ? "bg-canvas text-ink ring-1 ring-line" : "text-ink-subtle hover:text-ink"
  }`;
}

type FilterOption<T extends string> = {
  id: T;
  label: string;
  count?: number;
  disabled?: boolean;
};

function RailFilterMenu<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: FilterOption<T>[];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const selected = options.find((option) => option.id === value) ?? options[0];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-label={label}
        className={cx(
          "inline-flex max-w-full items-center gap-1 rounded-full border border-line bg-surface py-1 pr-2 pl-2.5 text-xs font-medium transition",
          open || value !== options[0]?.id ? "text-ink" : "text-ink-muted hover:text-ink",
        )}
      >
        <span className="truncate">{selected?.label}</span>
        <svg
          viewBox="0 0 16 16"
          fill="currentColor"
          className={cx("size-3.5 shrink-0 text-ink-subtle transition", open && "rotate-180")}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M4.22 5.97a.75.75 0 0 1 1.06 0L8 8.69l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.03a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open ? (
        <div
          id={menuId}
          role="listbox"
          aria-label={label}
          className="absolute top-full left-0 z-30 mt-1.5 max-h-64 w-max min-w-[9.5rem] max-w-[min(14rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain rounded-xl border border-line-strong bg-surface py-1 shadow-[0_8px_24px_-10px_rgba(15,18,24,0.18)]"
        >
          {options.map((option) => {
            const active = option.id === value;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={active}
                disabled={option.disabled}
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                className={cx(
                  "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition",
                  option.disabled
                    ? "cursor-not-allowed text-ink-subtle/50"
                    : active
                      ? "bg-canvas font-medium text-ink"
                      : "text-ink hover:bg-canvas",
                )}
              >
                <span className="flex w-3.5 shrink-0 items-center justify-center" aria-hidden="true">
                  {active ? (
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      className="size-3.5 text-money-600"
                    >
                      <path
                        d="m3.5 8.5 3 3 6-6.5"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.count !== undefined ? (
                  <span className="tabular shrink-0 text-xs text-ink-subtle">{option.count}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function RailEmpty({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div className="flex flex-col items-center px-3 py-10 text-center">
      <span
        className="inline-flex rounded-full bg-canvas p-3 text-ink-subtle/70"
        aria-hidden="true"
      >
        {icon}
      </span>
      <p className="mt-3 text-sm text-ink-subtle">{message}</p>
    </div>
  );
}

function RailRow({ collab, active }: { collab: CollabListRow; active: boolean }) {
  const status = collab.displayStatus;
  const needsYou = collabNeedsYou(collab);
  const track = listCounterpartTrack({
    yourRole: collab.yourRole,
    payer: collab.counterpartPayer,
    delivery: collab.counterpartDelivery,
  });

  return (
    <Link
      href={`/collabs/${collab.id}`}
      aria-current={active ? "page" : undefined}
      className={`flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition ${
        active ? "bg-canvas" : "hover:bg-canvas/60"
      }`}
    >
      <Avatar name={collab.counterpartName} url={collabCounterpartAvatarUrl(collab)} />

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            className={`min-w-0 truncate text-sm ${active ? "font-semibold text-ink" : "font-medium text-ink"}`}
          >
            {collab.counterpartName}
          </span>
          {track?.value ? (
            <TrackScoreBadge
              value={track.value}
              tone={track.tone}
              title={`${track.label} · ${track.detail}`}
            />
          ) : null}
          <BoostTierIcon tier={collab.boostTier} className="size-4" />
          {collab.muted ? (
            <span className="shrink-0 text-[10px] text-ink-subtle" title="Muted">
              muted
            </span>
          ) : null}
          <time
            className="tabular ml-auto shrink-0 text-[11px] text-ink-subtle"
            dateTime={new Date(collab.lastMessageAt ?? collab.createdAt).toISOString()}
          >
            {formatInboxStamp(collab.lastMessageAt ?? collab.createdAt)}
          </time>
        </div>

        <div className="mt-0.5 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-ink-muted">
            {collabPreview(collab) ?? `Offer opened · ${collab.reference}`}
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            {needsYou ? (
              <span
                title="Waiting on you"
                className={`size-2 rounded-full ${
                  collab.paymentDue ? "bg-money-600" : "bg-brand-600"
                }`}
              />
            ) : null}
            <span
              className={cx(
                "tabular text-[11px] font-medium",
                collab.compensationType === "barter" ? "text-ink-muted" : "text-money-700",
              )}
            >
              {collab.compensationType === "barter" ? "Barter" : formatINR(collab.amountTotal)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}

function SearchIcon({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M9 3.5a5.5 5.5 0 1 0 3.478 9.77l3.376 3.376a.75.75 0 1 0 1.06-1.06l-3.375-3.376A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
